/**
 * MessageList component.
 *
 * Scrollable container for chat messages. Renders the correct component
 * based on message type (text, list, card, system, typing). Manages
 * timestamp grouping, auto-scroll, and the typing indicator lifecycle.
 */

import DOMPurify from "dompurify";
import type {
  ResolvedWidgetConfig,
  ChatMessage,
  MessageCallbacks,
} from "../types";
import type { ConsentPrompt } from "./ConsentPrompt";
import type { InlineForm } from "./InlineForm";
import { TextBubble } from "./messages/TextBubble";
import { ListSelection } from "./messages/ListSelection";
import { PackageCard } from "./messages/PackageCard";
import { AppointmentList } from "./messages/AppointmentList";
import { SystemMessage } from "./messages/SystemMessage";
import { TypingIndicator } from "./messages/TypingIndicator";
import { parseMarkdown } from "./messages/markdown";
import { SuggestedPrompts } from "./SuggestedPrompts";
import {
  shouldShowTimestamp,
  createTimestampElement,
  formatTimestamp,
} from "./messages/timestamp";

/** Cadence for the relative-time ticker. 30s is the floor at which a
 *  message can transition from "Just now" to "1 min ago" within one
 *  refresh window of crossing the 60s boundary. */
const TIMESTAMP_REFRESH_INTERVAL_MS = 30_000;

export interface MessageListOptions {
  config: ResolvedWidgetConfig;
  callbacks?: MessageCallbacks;
  /**
   * Optional first-render prompt set. Used by the opening Terms-of-Use
   * acknowledgement gate to show only "OK" before the normal starters
   * are unlocked.
   */
  initialSuggestedPrompts?: string[];
}

export class MessageList {
  private readonly element: HTMLElement;
  private readonly welcomeEl: HTMLElement;
  private readonly config: ResolvedWidgetConfig;
  private consentPrompt: ConsentPrompt | null = null;
  private readonly typingIndicator: TypingIndicator;
  private suggestedPrompts: SuggestedPrompts | null = null;
  private lastMessageTimestamp: number = 0;
  private callbacks: MessageCallbacks;
  private timestampTickerId: ReturnType<typeof setInterval> | null = null;

  constructor(options: MessageListOptions) {
    const { config } = options;
    this.config = config;
    this.callbacks = options.callbacks ?? {};

    this.element = document.createElement("div");
    this.element.className = "hana-messages";
    this.element.setAttribute("role", "log");
    this.element.setAttribute("aria-label", "Chat messages");
    this.element.setAttribute("aria-live", "polite");
    this.element.setAttribute("aria-relevant", "additions");
    this.element.setAttribute("tabindex", "0");

    // Welcome message
    this.welcomeEl = document.createElement("div");
    this.welcomeEl.className = "hana-messages__welcome";
    this.welcomeEl.innerHTML = DOMPurify.sanitize(
      parseMarkdown(config.branding.welcomeMessage),
      {
        ALLOWED_TAGS: [
          "p",
          "br",
          "strong",
          "em",
          "a",
          "ul",
          "ol",
          "li",
          "code",
          "pre",
          "span",
        ],
        ALLOWED_ATTR: ["href", "target", "rel", "class"],
        ALLOW_DATA_ATTR: false,
      },
    );
    this.element.appendChild(this.welcomeEl);

    // Typing indicator (always at the bottom, toggled via show/hide)
    this.typingIndicator = new TypingIndicator();
    this.element.appendChild(this.typingIndicator.getElement());

    // REQ-D-046 — static empty-state starter pills. Rendered once,
    // directly under the welcome message. Tapping a pill sends it as
    // the first user turn via the onSuggestedPrompt callback (the
    // ChatWidget routes that through its normal send path) and the
    // whole row collapses since the empty state is over.
    this.renderSuggestedPrompts(
      options.initialSuggestedPrompts ?? config.branding.suggestedPrompts ?? [],
    );

    // Refresh relative timestamps periodically so "Just now" rolls
    // over to "1 min ago" / "5 min ago" / "1 hr ago" without the
    // user having to interact with the chat. Each TextBubble +
    // SystemMessage stamps its message epoch ms on `data-message-timestamp`
    // so the ticker can recompute without holding state.
    this.startTimestampTicker();
  }

  private renderSuggestedPrompts(prompts: string[]): void {
    if (prompts.length === 0) return;
    const promptConfig = {
      ...this.config,
      branding: {
        ...this.config.branding,
        suggestedPrompts: prompts,
      },
    };
    const suggestedPrompts = new SuggestedPrompts({
      config: promptConfig,
      onSelect: (prompt: string) => {
        this.callbacks.onSuggestedPrompt?.(prompt);
      },
    });
    if (suggestedPrompts.hasPrompts()) {
      this.suggestedPrompts = suggestedPrompts;
      this.element.insertBefore(
        suggestedPrompts.getElement(),
        this.typingIndicator.getElement(),
      );
    }
  }

  private startTimestampTicker(): void {
    if (typeof window === "undefined") return; // SSR guard
    this.timestampTickerId = setInterval(() => {
      this.refreshAllTimestamps();
    }, TIMESTAMP_REFRESH_INTERVAL_MS);
  }

  private refreshAllTimestamps(): void {
    const now = Date.now();
    const els = this.element.querySelectorAll<HTMLElement>(
      "[data-message-timestamp]",
    );
    els.forEach((el) => {
      const ts = Number(el.getAttribute("data-message-timestamp"));
      if (Number.isFinite(ts) && ts > 0) {
        el.textContent = formatTimestamp(ts, now);
      }
    });
  }

  /**
   * Stop the relative-timestamp ticker. Called by the embedder during
   * widget teardown so the interval doesn't leak past the chat session.
   */
  destroy(): void {
    if (this.timestampTickerId !== null) {
      clearInterval(this.timestampTickerId);
      this.timestampTickerId = null;
    }
  }

  /**
   * Update the status indicator on an already-rendered user message.
   * No-ops if the message id is not found (e.g., bubble has scrolled
   * past the live region or the lookup raced a clear()). Used by
   * ChatWidget to flip "sending" → "sent" → "delivered" once the
   * transport ack / first bot frame arrives.
   */
  setUserMessageStatus(messageId: string, status: "sent" | "delivered"): void {
    const bubble = this.element.querySelector<HTMLElement>(
      `.hana-bubble--user[data-message-id="${CSS.escape(messageId)}"]`,
    );
    if (!bubble) return;
    const footer = bubble.querySelector<HTMLElement>(".hana-bubble__footer");
    if (!footer) return;
    const oldStatus = footer.querySelector<HTMLElement>(".hana-status");
    // Lazy-import the factory to avoid a top-of-file circular ref.
    import("./messages/MessageStatus").then(({ createStatusIndicator }) => {
      const next = createStatusIndicator(status);
      if (oldStatus) footer.replaceChild(next, oldStatus);
      else footer.appendChild(next);
    });
  }

  /**
   * REQ-D-046 — collapse the static empty-state starter pills.
   * Idempotent; safe to call on every message append.
   */
  private dismissSuggestedPrompts(): void {
    if (this.suggestedPrompts) {
      this.suggestedPrompts.dismiss();
      this.suggestedPrompts = null;
    }
  }

  /**
   * Replace the currently visible starter prompts without adding a
   * chat message. The opening ToU gate uses this to swap the initial
   * single "OK" acknowledgement action for the normal entity starters
   * after acknowledgement, while keeping user input off the backend
   * until the gate is satisfied.
   */
  replaceSuggestedPrompts(prompts: string[]): void {
    this.dismissSuggestedPrompts();
    this.renderSuggestedPrompts(prompts);
    this.scrollToBottom();
  }


  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Set message callbacks (for list selection, card actions).
   * Can be called after construction to wire up handlers.
   */
  setCallbacks(callbacks: MessageCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Scroll to the bottom of the message list.
   * Called after new messages are appended.
   */
  scrollToBottom(): void {
    this.element.scrollTop = this.element.scrollHeight;
  }

  /**
   * Append a typed ChatMessage to the list.
   * Renders the correct component based on msg.type,
   * inserts timestamp separators as needed, and auto-scrolls.
   */
  appendChatMessage(msg: ChatMessage): void {
    // REQ-D-046 — the empty state is over the moment any message is
    // rendered; collapse the static starter pills so they don't
    // linger mid-conversation.
    this.dismissSuggestedPrompts();

    // Dedup by message_id. The backend's replay buffer re-sends every
    // buffered bot_response on each WS reconnect; without this guard
    // the same logical message renders as N stacked bubbles. We do
    // NOT dedup user messages because the optimistic-render id is
    // unique per send and never replayed by the server.
    if (msg.id) {
      const existing = this.element.querySelector<HTMLElement>(
        `[data-message-id="${CSS.escape(msg.id)}"]`,
      );
      if (existing) {
        // Already rendered — silently drop the duplicate.
        return;
      }
    }

    // Insert timestamp separator if needed
    if (
      this.lastMessageTimestamp > 0 &&
      shouldShowTimestamp(this.lastMessageTimestamp, msg.timestamp)
    ) {
      const tsEl = createTimestampElement(msg.timestamp);
      this.element.insertBefore(tsEl, this.typingIndicator.getElement());
    }

    const component = this.createComponent(msg);
    if (component) {
      this.element.insertBefore(component, this.typingIndicator.getElement());
    }

    this.lastMessageTimestamp = msg.timestamp;
    this.scrollToBottom();
  }

  /**
   * Append a raw DOM element to the message list.
   * Kept for backward compatibility with consent prompts
   * and other non-ChatMessage elements.
   */
  appendMessage(messageEl: HTMLElement): void {
    // REQ-D-046 — any rendered content ends the empty state.
    this.dismissSuggestedPrompts();
    this.element.insertBefore(messageEl, this.typingIndicator.getElement());
    this.scrollToBottom();
  }

  /**
   * GH #792: Render a WhatsApp transition notice as a visually distinct
   * banner (not a regular chat bubble). Informational only — input
   * stays enabled so the patient can continue chatting in a new session.
   */
  appendTransitionNotice(text: string): void {
    this.dismissSuggestedPrompts();

    const separator = document.createElement("div");
    separator.className = "hana-messages__transition-separator";

    const notice = document.createElement("div");
    notice.className = "hana-messages__transition-notice";
    notice.textContent = text;

    this.element.insertBefore(separator, this.typingIndicator.getElement());
    this.element.insertBefore(notice, this.typingIndicator.getElement());
    this.scrollToBottom();
  }

  /**
   * Show the typing indicator.
   */
  showTyping(): void {
    this.typingIndicator.show();
    this.scrollToBottom();
  }

  /**
   * Hide the typing indicator.
   */
  hideTyping(): void {
    this.typingIndicator.hide();
  }

  /**
   * Check if the typing indicator is currently visible.
   */
  isTypingVisible(): boolean {
    return this.typingIndicator.isVisible();
  }

  /**
   * Append a consent prompt to the message list.
   * Only one consent prompt is shown per session. If a consent prompt
   * already exists, this call is ignored (no duplicate prompts).
   */
  appendConsentPrompt(prompt: ConsentPrompt): void {
    if (this.consentPrompt !== null) return;
    // REQ-D-046 — any rendered content ends the empty state.
    this.dismissSuggestedPrompts();
    this.consentPrompt = prompt;
    this.element.insertBefore(
      prompt.getElement(),
      this.typingIndicator.getElement(),
    );
    this.scrollToBottom();
  }

  /**
   * Get the currently rendered consent prompt, if any.
   */
  getConsentPrompt(): ConsentPrompt | null {
    return this.consentPrompt;
  }

  /**
   * Append an inline form to the message list.
   * Forms are rendered as special messages within the chat flow.
   */
  appendForm(form: InlineForm): void {
    // REQ-D-046 — any rendered content ends the empty state.
    this.dismissSuggestedPrompts();
    this.element.insertBefore(
      form.getElement(),
      this.typingIndicator.getElement(),
    );
    this.scrollToBottom();
  }

  /**
   * Clear all messages except the welcome message and typing indicator.
   */
  clear(): void {
    while (
      this.element.lastChild &&
      this.element.lastChild !== this.welcomeEl
    ) {
      this.element.removeChild(this.element.lastChild);
    }
    this.consentPrompt = null;
    this.lastMessageTimestamp = 0;
    // The starter-pill DOM (if still present) was removed by the
    // loop above; drop the dangling reference so we don't double-free.
    this.suggestedPrompts = null;

    // Re-append typing indicator (it was removed during clear)
    this.element.appendChild(this.typingIndicator.getElement());
    this.typingIndicator.hide();
  }

  /**
   * Create the appropriate component for a ChatMessage.
   */
  private createComponent(msg: ChatMessage): HTMLElement | null {
    switch (msg.type) {
      case "text": {
        const bubble = new TextBubble({ message: msg });
        return bubble.getElement();
      }
      case "list": {
        const list = new ListSelection({
          message: msg,
          onSelect: (itemId: string) => {
            this.callbacks.onListSelect?.(itemId);
          },
        });
        return list.getElement();
      }
      case "card": {
        const card = new PackageCard({
          message: msg,
          onAction: (cardId: string) => {
            this.callbacks.onCardAction?.(cardId);
          },
        });
        return card.getElement();
      }
      case "appointment_list": {
        const aptList = new AppointmentList({
          message: msg,
          callbacks: {
            onSelect: (appointmentId: string) => {
              this.callbacks.onAppointmentSelect?.(appointmentId);
            },
          },
        });
        return aptList.getElement();
      }
      case "system": {
        const system = new SystemMessage({ message: msg });
        return system.getElement();
      }
      case "typing":
        // Typing messages are handled via showTyping/hideTyping
        this.showTyping();
        return null;
      default:
        return null;
    }
  }
}
